<template>
  <section>
    <b-field grouped group-multiline>
      <b-field>
        <b-select
          v-model="selectedArea"
          :loading="loadingAreas"
          :placeholder="$t('message.please_select') + $t('attributes.area')"
          @input="onFilter"
        >
          <option value="" disabled>
            {{ $t('message.please_select') + $t('attributes.area') }}
          </option>
          <option v-for="option in areas" :value="option" :key="option.code">
            {{ option.name }}
          </option>
        </b-select>
      </b-field>
      <b-field>
        <b-button
          :loading="sending"
          class="is-primary"
          icon-left="plus"
          @click="onAdd"
        >
          {{
            $t('attributes.add') +
              $t('attributes.honey_moon_page') +
              $t('attributes.travel_group')
          }}
        </b-button>
      </b-field>
    </b-field>
    <b-table
      :data="travelGroups"
      :loading="loadingTravelGroups"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        centered
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>
      <b-table-column
        field="group_code"
        :label="$t('attributes.group_code')"
        centered
        v-slot="props"
      >
        {{ props.row.group_code }}
      </b-table-column>
      <b-table-column
        :label="$t('attributes.picture')"
        width="120"
        centered
        v-slot="props"
      >
        <a
          v-if="props.row.picture"
          :href="projectImageUrl(props.row.picture)"
          @click.prevent="onClickImage(props.row.picture)"
        >
          <figure class="image">
            <img
              :src="projectImageUrl(props.row.picture)"
              :alt="$t('attributes.picture')"
              style="width: 120px; margin: auto;"
            />
          </figure>
        </a>
      </b-table-column>
      <b-table-column
        field="group_departure_date"
        :label="$t('attributes.departure_date')"
        width="120"
        centered
        v-slot="props"
      >
        {{ props.row.group_departure_date.split('T')[0] }}
      </b-table-column>
      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        centered
        v-slot="props"
      >
        <p v-html="props.row.name"></p>
      </b-table-column>
      <b-table-column
        :label="$t('attributes.function')"
        width="250"
        centered
        v-slot="props"
      >
        <b-button type="is-info" @click.prevent.stop="onModify(props.row)">
          {{ $t('attributes.modify') }}
        </b-button>
        <b-button
          type="is-danger"
          @click.prevent.stop="onDeleteConfirm(props.row)"
          style="margin-left: 15px"
        >
          {{ $t('attributes.delete') }}
        </b-button>
      </b-table-column>
    </b-table>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
    <b-modal :active.sync="isModalActive">
      <honey-moon-travel-group-form
        :travel-group="selectedTravelGroup"
        :isCreate="isCreate"
        @send="onSend"
      ></honey-moon-travel-group-form>
    </b-modal>
  </section>
</template>

<script>
import { AREA_API, HONEY_MOON_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'
import HoneyMoonTravelGroupForm from '@/components/company/HoneyMoonTravelGroupForm'

export default {
  name: 'HoneyMoonTravelGroupList',
  components: { HoneyMoonTravelGroupForm },
  data() {
    return {
      areas: [],
      selectedArea: null,
      loadingAreas: false,

      loadingTravelGroups: false,
      travelGroups: [],
      selectedTravelGroup: null,
      total: 0,
      sortField: 'sort',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',
      page: 1,
      perPage: 15,

      isImageModalActive: false,
      selectedImageUrl: '',

      isCreate: false,
      isModalActive: false,
      sending: false
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        this.areas = res.data.data.sort(a => a.sort)
        this.selectedArea = null
      } catch (e) {
        this.areas = []
        this.selectedArea = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadTravelGroups() {
      const api = `${HONEY_MOON_API}/list/travel_group`
      try {
        this.loadingTravelGroups = true
        const params = {
          areaCode: this.selectedArea.code,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }
        const res = await this.axios.get(api, { params })
        this.travelGroups = res.data.data.items
        this.total = res.data.data.item_count
      } catch (e) {
        this.travelGroups = []
        this.total = 0

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingTravelGroups = false
      }
    },
    async onSend(item) {
      if (this.isCreate) {
        await this.createTravelGroup(item)
      } else {
        await this.updateTravelGroup(item)
      }
    },
    async updateTravelGroup(item) {
      const api = `${HONEY_MOON_API}/update/travel_group`

      try {
        await this.axios.put(api, item)
        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadTravelGroups()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.loadingTravelGroups = false
      }
    },
    async createTravelGroup(item) {
      const api = `${HONEY_MOON_API}/create/travel_group`

      try {
        this.sending = true
        await this.axios.post(api, item)
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadTravelGroups()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    onModify(item) {
      this.selectedTravelGroup = item
      this.isCreate = false
      this.isModalActive = true
    },
    onAdd() {
      this.selectedTravelGroup = {
        id: '',
        group_code: '',
        group_departure_date: '',
        sort: 1,
        status: true,
        name: '',
        picture: '',
        remark: '',
        product_code: '',
        catalog_no: ''
      }
      this.isCreate = true
      this.isModalActive = true
    },
    onDeleteConfirm(item) {
      this.$buefy.dialog.confirm({
        message:
          '<span style="color:Red; font-size: 18px; margin-bottom: 15px;">確定要刪除旅行團?</span><br>' +
          item.group_code +
          '<br>' +
          item.name,
        onConfirm: () => this.onDelete(item)
      })
    },
    async onDelete(item) {
      const api = `${HONEY_MOON_API}/delete/travel_group/${item.group_code}`
      this.sending = true

      try {
        await this.axios.delete(api)
        notifyService.success(this.$t('attributes.delete_succeed'))

        await this.loadTravelGroups()
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    },
    onPageChange(page) {
      this.page = page
      this.loadTravelGroups()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadTravelGroups()
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = this.projectImageUrl(url)
    },
    onFilter: debounce(function() {
      this.loadTravelGroups()
    }, 500)
  }
}
</script>

<style scoped></style>
